import React, { useState, useEffect } from 'react';
import { Thermometer, Info } from 'lucide-react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useAISettings } from '../../../hooks/useAISettings';

export function TemperatureSettings() {
  const { chatbotPublicId } = useChatbot();
  const { temperature, updateTemperature, loading } = useAISettings();
  const [value, setValue] = useState(temperature);
  const [isDragging, setIsDragging] = useState(false);

  // Sync local value with store value
  useEffect(() => {
    if (!isDragging) {
      setValue(temperature);
    }
  }, [temperature, isDragging]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    setValue(newValue);
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (chatbotPublicId && value !== temperature) {
      updateTemperature(value, chatbotPublicId);
    }
  };

  // Get description based on temperature value
  const getTemperatureDescription = (temp: number) => {
    if (temp < 0.3) return "Very deterministic and consistent. AI generates highly predictable responses.";
    if (temp < 0.7) return "Balanced. AI generates consistent responses with slight variability.";
    if (temp < 1.0) return "Creative. AI generates more varied and creative responses.";
    if (temp < 1.5) return "Very creative. AI takes more risks and generates more varied responses.";
    return "Extremely random. AI can generate highly diverse and sometimes unpredictable responses.";
  };

  // Temperature color based on value
  const getTemperatureColor = (temp: number) => {
    if (temp < 0.3) return "bg-blue-500";
    if (temp < 0.7) return "bg-green-500";
    if (temp < 1.0) return "bg-yellow-500";
    if (temp < 1.5) return "bg-orange-500";
    return "bg-red-500";
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
      <div className="flex items-center mb-4">
        <Thermometer className="w-5 h-5 text-blue-400 mr-2" />
        <h3 className="text-lg font-semibold text-white">AI Temperature</h3>
      </div>

      <div className="text-gray-400 mb-6 space-y-2">
        <p>
          Temperature controls the level of creativity and variability in the responses generated by the AI.
        </p>
        <div className="flex items-start p-3 bg-white/5 rounded-lg mt-2">
          <Info className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="mb-1">
              <span className="font-medium text-white">Low temperature (0-0.5):</span> More consistent and deterministic responses, ideal for factual assistants.
            </p>
            <p className="mb-1">
              <span className="font-medium text-white">Medium temperature (0.5-1.0):</span> Good balance between creativity and consistency.
            </p>
            <p>
              <span className="font-medium text-white">High temperature (1.0-2.0):</span> More creative and varied responses, but potentially less accurate.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">Deterministic</span>
          <span className="font-medium text-white">{value.toFixed(1)}</span>
          <span className="text-sm text-gray-400">Creative</span>
        </div>

        <div className="relative py-1">
          <input
            type="range"
            min="0"
            max="2"
            step="0.1"
            value={value}
            onChange={handleChange}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchEnd={handleMouseUp}
            className="w-full accent-blue-500 cursor-pointer"
          />
          <div 
            className="absolute left-0 bottom-0 h-1 rounded-full" 
            style={{ 
              width: `${(value / 2) * 100}%`,
              background: 'linear-gradient(to right, rgb(59, 130, 246), rgb(52, 211, 153), rgb(249, 115, 22))'
            }}
          />
        </div>

        <div className="mt-2">
          <div className={`p-3 rounded-lg ${getTemperatureColor(value)}/10 border ${getTemperatureColor(value)}/20`}>
            <p className="text-sm text-gray-300">
              {getTemperatureDescription(value)}
            </p>
          </div>
        </div>

        {loading && (
          <div className="text-sm text-gray-400 mt-2">Saving...</div>
        )}
      </div>
    </div>
  );
}